import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
// react-lazyload v3 does not execute window/document calls at module import time —
// all browser access is deferred to componentDidMount, so this import is SSR-safe.
import { forceCheck } from "react-lazyload";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "../../../video-lab/dtv-text";
import { KeyValuePair } from "@directv-digital/dds.services.channel-lineup-provider";
import { ChannelCardV2, denormalizeChannels, denormalizeCategorizedChannels } from "lib/helper/channel-interactive-tool-v2";
import { IChnlInteractiveThemeUI } from "lib/helper/channel-interactive-tool-theme";
import { LocalRsnApiResponse } from "@directv-digital/dds.apis.local-rsn-chnls";
import { County } from "@directv-digital/dds.apis.county-lookup";
import CategorySection from "./CategorySection";
import ChannelIcon from "./ChannelIcon";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import {
  mainContainerStyles,
  addonSectionContainerStyles,
  addonHeaderContainerStyles,
  addonTitleStyles,
  addonSubtitleStyles,
  addonCategorySectionStyles,
  addonCategoryTitleStyles,
  addonChannelIconsContainerStyles,
  COLLAPSED_MAX_HEIGHT_XS,
  HIDDEN_ROW_MIN_XS
} from "./styles";

interface IconBannerV2Props {
  categories: KeyValuePair[];
  channelsByCategory: Record<string, string[]> | Record<string, ChannelCardV2[]> | Record<string, ChannelCardV2[] | string[]>;
  channelMap?: Record<string, ChannelCardV2>;
  onChannelClick: (channel: ChannelCardV2) => void;
  theme?: IChnlInteractiveThemeUI;
  isSearchData?: boolean;
  addOnChannelsByCategory?: Record<string, string[]> | Record<string, ChannelCardV2[]>;
  allCategories?: KeyValuePair[];
  genreEnabled?: boolean;
  zipCodeModule?: any;
  myHomeTeamModalCta?: { label: string; targeturl: string };
  tooltips?: any;
  showMoreData?: Record<string, boolean>;
  updateShowMoreCategories?: (data: Record<string, boolean>) => void;
  openChannelModal?: boolean;
  // Zipcode module handler props (threaded to CategorySection for MYHOMETEAM)
  onZipcodeSearch?: (zipcode: string, counties: County[] | null, rsnFee?: string, localRsnChnls?: LocalRsnApiResponse | null, county?: County) => void;
  onLocalRsnApiError?: (error?: string) => void;
  onLocalRsnChnlsConfirmed?: (channels: LocalRsnApiResponse | null, dma?: string, nielsenDma?: string) => void;
  onCacheConfirmed?: (zipcode: string, counties: County[] | null, rsnFee?: string, localRsnChnls?: LocalRsnApiResponse | null, county?: County) => void;
  localRsnApiOptions?: { satellite?: boolean; stream?: boolean };
  zipcodeInitData?: {
    zipcode?: string;
    counties?: County[] | null;
    county?: County | null;
    rsnFee?: string;
    localRsnChnls?: LocalRsnApiResponse | null;
  } | null;
}

const IconBannerV2: React.FC<IconBannerV2Props> = ({
  categories,
  channelsByCategory,
  channelMap = {},
  onChannelClick,
  theme,
  isSearchData = false,
  addOnChannelsByCategory = {},
  allCategories = [],
  genreEnabled = false,
  zipCodeModule,
  myHomeTeamModalCta,
  tooltips,
  showMoreData = {},
  updateShowMoreCategories,
  openChannelModal = true,
  onZipcodeSearch,
  onLocalRsnApiError,
  onLocalRsnChnlsConfirmed,
  onCacheConfirmed,
  localRsnApiOptions,
  zipcodeInitData
}) => {
  try {
    const [showMoreKeys, setShowMoreKeys] = useState<Record<string, boolean>>(showMoreData);

    // Ref attached to the root banner element (used by calcShowMore recalculation)
    const bannerRef = useRef<HTMLDivElement>(null);

    // Ref to always hold the latest categories without making it a useEffect dependency
    const categoriesRef = useRef(categories);
    useEffect(() => {
      categoriesRef.current = categories;
    }, [categories]);

    // Denormalize channels if they're string arrays (check per category)
    const denormalizedChannelsByCategory = useMemo(() => {
      const result: Record<string, ChannelCardV2[]> = {};

      Object.entries(channelsByCategory).forEach(([categoryKey, channels]) => {
        if (!channels || channels.length === 0) {
          result[categoryKey] = [];
          return;
        }

        // Check if this category's channels are strings or objects
        const firstChannel = channels[0];
        let resolved: ChannelCardV2[];

        if (typeof firstChannel === "string") {
          // Category may be a mixed array: server string-refs followed by injected full objects
          // (e.g. genre categories after local/RSN injection).
          // Resolve each entry individually: strings → channelMap lookup, objects → pass through.
          const hasAnyObjects = (channels as Array<string | ChannelCardV2>).some(ch => typeof ch !== "string");
          if (hasAnyObjects) {
            resolved = (channels as Array<string | ChannelCardV2>).reduce<ChannelCardV2[]>((acc, ch) => {
              if (typeof ch === "string") {
                const found = channelMap[ch];
                if (found) {
                  acc.push(found);
                }
              } else {
                acc.push(ch);
              }
              return acc;
            }, []);
          } else {
            // Pure string array — use fast denormalize path
            resolved = denormalizeChannels(channels as string[], channelMap);
          }
        } else {
          // Already denormalized (pure object array)
          resolved = channels as ChannelCardV2[];
        }

        // In genre variant, sort alphabetically by label after all channels are resolved.
        // Ensures injected local/RSN channels are interleaved naturally rather than appended at end.
        if (genreEnabled && resolved.some(ch => ch.isLocalChannel)) {
          resolved = [...resolved].sort((a, b) => (a.label || "").localeCompare(b.label || ""));
        }

        result[categoryKey] = resolved;
      });

      return result;
    }, [channelsByCategory, channelMap, genreEnabled]);

    // Denormalize add-on channels if needed
    const denormalizedAddOnChannelsByCategory = useMemo(() => {
      const firstCategoryKey = Object.keys(addOnChannelsByCategory)[0];
      if (!firstCategoryKey) {
        return {};
      }

      const firstChannel = addOnChannelsByCategory[firstCategoryKey]?.[0];
      if (typeof firstChannel === "string") {
        return denormalizeCategorizedChannels(addOnChannelsByCategory as Record<string, string[]>, channelMap);
      }
      return addOnChannelsByCategory as Record<string, ChannelCardV2[]>;
    }, [addOnChannelsByCategory, channelMap]);

    const calcShowMore = useCallback(
      (iconCtnr: HTMLElement | null) => {
        if (!iconCtnr) {
          return;
        }

        // eslint-disable-next-line camelcase
        const ctg_key = iconCtnr?.getAttribute("id")?.split("-")[2];
        const smElem: HTMLElement | null = iconCtnr.nextElementSibling as HTMLElement;
        const { scrollHeight, clientHeight } = iconCtnr;

        if (iconCtnr && smElem) {
          // Both height tests are measured against HIDDEN_ROW_MIN_XS (half a tile) rather than
          // exactly, because the logo image paints ~1.3px outside its pinned tile: every
          // category reports scrollHeight a pixel over clientHeight even with nothing hidden,
          // and the old exact `clientHeight < scrollHeight` turned "Show More" on for one-row
          // categories as soon as any resize/scroll re-ran this. See HIDDEN_ROW_MIN_XS.
          const hasHiddenRow = scrollHeight - clientHeight >= HIDDEN_ROW_MIN_XS;
          // Collapsed, clientHeight IS the cap, so this repeats hasHiddenRow. It earns its keep
          // on an EXPANDED grid (clientHeight === scrollHeight, so nothing reads as hidden) that
          // this instance's showMoreKeys does not know about — reachable because the ids below
          // are global: SearchResultBanner mounts a SECOND IconBannerV2 with the same
          // `chnlCtg-icons-<key>` ids, so getElementById can hand us the other instance's grid
          // while showMoreKeys is ours. Without this a long grid can be left open with no
          // "Show Less" to close it.
          const exceedsCollapsedCap = scrollHeight - COLLAPSED_MAX_HEIGHT_XS >= HIDDEN_ROW_MIN_XS;
          // Check window.innerWidth directly instead of using isMobile state
          // eslint-disable-next-line camelcase
          const shouldShow = (hasHiddenRow || exceedsCollapsedCap || showMoreKeys?.[ctg_key || ""]) && window?.innerWidth < 600;
          smElem.style.display = shouldShow ? "block" : "none";
          smElem.style.visibility = shouldShow ? "visible" : "hidden";
        } else if (smElem) {
          smElem.style.display = "none";
          smElem.style.visibility = "hidden";
        }
      },
      [showMoreKeys] // Removed isMobile dependency
    );

    const addOnCategories = useMemo(() => Object.keys(denormalizedAddOnChannelsByCategory)
      .map(key => allCategories.find(cat => cat.key === key) || categories.find(cat => cat.key === key))
      .filter(Boolean) as KeyValuePair[], [denormalizedAddOnChannelsByCategory, allCategories, categories]);

    useEffect(() => {
      const recalcShowMore = () => {
        categoriesRef.current.forEach(category => {
          const iconCtnr = document.getElementById(`chnlCtg-icons-${category.key}`);
          if (iconCtnr) {
            calcShowMore(iconCtnr);
          }
        });
      };

      // ResizeObserver on the banner root: fires whenever icon images load and
      // cause a layout reflow, which is exactly when scrollHeight becomes accurate.
      // This replaces the unreliable 100ms setTimeout that ran before images loaded.
      let roTimeout: NodeJS.Timeout;
      let observer: ResizeObserver | null = null;
      if (typeof ResizeObserver !== "undefined" && bannerRef.current) {
        observer = new ResizeObserver(() => {
          // Debounce rapid consecutive fires (e.g., multiple images loading at once)
          clearTimeout(roTimeout);
          roTimeout = setTimeout(recalcShowMore, 50);
        });
        observer.observe(bannerRef.current);
      } else {
        // Fallback for environments without ResizeObserver
        setTimeout(recalcShowMore, 300);
      }

      // Debounce the resize handler to avoid excessive DOM calculations
      let resizeTimeout: NodeJS.Timeout;
      const debouncedRecalcShowMore = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          recalcShowMore();
        }, 250); // Wait 250ms after resize stops before recalculating
      };

      window.addEventListener("resize", debouncedRecalcShowMore);

      return () => {
        window.removeEventListener("resize", debouncedRecalcShowMore);
        clearTimeout(resizeTimeout);
        clearTimeout(roTimeout);
        observer?.disconnect();
      };
    }, [calcShowMore]); // categoriesRef.current used so categories never need to be a dep

    // Problem: react-lazyload listens to #channels-container's scroll events (overflow mode),
    // not the page's. When the user scrolls the PAGE to bring the banner into view,
    // no container scroll fires, so icons stay blank until the user scrolls inside.
    //
    // Fix: add a window scroll listener that calls forceCheck() on every page scroll
    // while the banner is in the page viewport. The IntersectionObserver tracks enter/exit.
    //
    // Why page scroll (not just one forceCheck on entry): with threshold:0 the observer
    // fires when even 1px enters the viewport. At that point checkOverflowVisible sees
    // intersectionHeight≈0 and only loads the first row. As the page scrolls further,
    // intersectionHeight grows to the full container height — each scroll event loads
    // the next visible batch. LazyLoad's own `once` prop prevents any double-loading.
    useEffect(() => {
      const element = bannerRef.current;
      if (!element || typeof IntersectionObserver === "undefined") {
        return;
      }

      let rafId: number;
      let isVisible = false;

      const handlePageScroll = () => {
        if (!isVisible) {
          return;
        }
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => forceCheck());
      };

      const observer = new IntersectionObserver(
        ([entry]) => {
          isVisible = entry.isIntersecting;
          if (entry.isIntersecting) {
            // Immediate check when first entering viewport (before any scroll fires)
            rafId = requestAnimationFrame(() => forceCheck());
          }
        },
        { threshold: 0 }
      );

      observer.observe(element);
      window.addEventListener("scroll", handlePageScroll, { passive: true });

      return () => {
        observer.disconnect();
        window.removeEventListener("scroll", handlePageScroll);
        cancelAnimationFrame(rafId);
      };
    }, []);

    if (!categories?.length) {
      return null;
    }

    // eslint-disable-next-line camelcase
    const onShowMore = (ctg_key: string) => {
      // eslint-disable-next-line camelcase
      const nextSMVal = !(showMoreData?.[ctg_key] || false);
      const nextSM = {
        ...showMoreData,
        // eslint-disable-next-line camelcase
        [ctg_key]: nextSMVal
      };
      // eslint-disable-next-line camelcase
      const iconCtnr: HTMLElement | null = document.getElementById(`chnlCtg-icons-${ctg_key}`) || null;
      if (iconCtnr) {
        // `none` (not a fixed 5000px cap) so a long expanded lineup is never
        // clipped; trades the slide animation for an instant open on mobile.
        // Collapsing back must land on the same row boundary the CSS uses (WAT-11563).
        iconCtnr.style.maxHeight = nextSMVal === true ? "none" : `${COLLAPSED_MAX_HEIGHT_XS}px`;
      }
      setShowMoreKeys(nextSM);
      updateShowMoreCategories?.(nextSM);
    };

    const handleChannelClick = (channel: ChannelCardV2) => {
      const hasPackages = channel.hasIncludedPackages || (channel.signaturePackages?.length || 0) > 0 || (channel.genrePackages?.length || 0) > 0;

      if (hasPackages) {
        onChannelClick(channel);
      }
    };

    return (
      <DtvBox id="icon-banner-v2-main" ref={bannerRef} sx={mainContainerStyles}>
        {/* Regular Categories */}
        {categories.map((category, categoryIndex) => {
          const channels = denormalizedChannelsByCategory?.[category.key] || [];

          return (
            <CategorySection
              calcShowMore={calcShowMore}
              category={category}
              categoryIndex={categoryIndex}
              channels={channels}
              genreEnabled={genreEnabled}
              key={category.key}
              localRsnApiOptions={localRsnApiOptions}
              myHomeTeamModalCta={myHomeTeamModalCta}
              openChannelModal={openChannelModal}
              showMoreKeys={showMoreKeys}
              theme={theme}
              tooltips={tooltips}
              zipCodeModule={zipCodeModule}
              zipcodeInitData={zipcodeInitData}
              onCacheConfirmed={onCacheConfirmed}
              onChannelClick={onChannelClick}
              onLocalRsnApiError={onLocalRsnApiError}
              onLocalRsnChnlsConfirmed={onLocalRsnChnlsConfirmed}
              onShowMore={onShowMore}
              onZipcodeSearch={onZipcodeSearch}
            />
          );
        })}

        {/* Add-on Channels Section */}
        {isSearchData && addOnCategories.length > 0 && (
          <DtvBox component="section" id="ChnlListingPage-add-on-channels" sx={addonSectionContainerStyles}>
            <DtvBox sx={addonHeaderContainerStyles}>
              <DtvText content="ADD-ON CHANNELS" sx={addonTitleStyles} variant="subhead1medium" />
              <DtvText content="Available for an additional cost." sx={addonSubtitleStyles} variant="body1" />
            </DtvBox>

            {addOnCategories.map(category => {
              const channels = denormalizedAddOnChannelsByCategory[category.key] || [];
              if (!channels.length) {
 return null;
}

              return (
                <DtvBox key={`addon-${category.key}`} sx={addonCategorySectionStyles}>
                  <DtvText content={category.value?.toUpperCase()} sx={addonCategoryTitleStyles} variant="body2medium" />
                  <DtvBox id={`addon-chnlCtg-icons-${category.key}`} sx={addonChannelIconsContainerStyles}>
                    {channels.map((channel, index) => {
                      const hasPackages = channel.hasIncludedPackages || (channel.signaturePackages?.length || 0) > 0 || (channel.genrePackages?.length || 0) > 0;
                      const isClickable = hasPackages && openChannelModal;

                      return (
                        <ChannelIcon
                          category={category}
                          channel={channel}
                          genreEnabled={genreEnabled}
                          index={index}
                          isClickable={isClickable}
                          key={`${channel.id}-${index}`}
                          theme={theme}
                          onClick={handleChannelClick}
                        />
                      );
                    })}
                  </DtvBox>
                </DtvBox>
              );
            })}
          </DtvBox>
        )}
      </DtvBox>
    );
  } catch (error) {
    console.error("Error in IconBannerV2 >> ", error);
    return <ErrorDisplay component="Icon Banner V2" error={error} />;
  }
};

export default React.memo(IconBannerV2);
